<template>
  <div>
    <admin-wrapper :inputs="inputs" :has-buttons="false">
      <template #table.input>
        <v-row no-gutters>
          <v-col cols="6">
            <v-row class="pb-4" no-gutters>
              <v-col cols="12">
                <v-row align="center" no-gutters>
                  <v-col cols="auto" class="text-h6 font-weight-bold pr-3">
                    {{ $i18n.t('admin.scadaTabAttributes') }}
                  </v-col>
                  <v-col cols="auto">
                    <base-rounded-button
                      icon="mdi-plus"
                      size="x-small"
                      color="success"
                      :tooltip="$i18n.t('button.addNewAttribute')"
                      @click="openAddingDialog()"
                    />
                  </v-col>
                  <v-spacer />
                  <v-col cols="5">
                    <data-input
                      v-model="search"
                      :data-type="{
                        name: 'text',
                      }"
                      prepend-inner-icon="mdi-magnify"
                      :placeholder="$i18n.t('default.find')"
                      hide-details
                      clearable
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" style="border: 1px solid rgba(0, 0, 0, 0.12)">
                <v-data-table
                  :headers="headers"
                  :items="items"
                  :search="search"
                  :loading="isFetching"
                  :footer-props="{
                    itemsPerPageOptions: [10, 20, 50],
                  }"
                  calculate-widths
                  disable-sort
                  dense
                >
                  <template #item.actions="{item}">
                    <dots-menu :items="dotsItems" @delete="openDeletingDialog(item)" />
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </admin-wrapper>
    <main-input-dialog
      v-if="isAddingDialogLoaded"
      :is-visible.sync="isAddingDialogVisible"
      :title-translation="$i18n.t('dialog.title.addScadaAttribute')"
      agree-text="dialog.agreeAdd"
      :inputs="scadaInputs"
      :loading="isLoading"
      body-height="30vh"
      dialog-width="30%"
      @agree="addNewAttribute"
    >
      <template v-for="input of scadaInputs" #[`${input.name}.input`]="data">
        <v-col :key="input.name" cols="6">
          <data-input v-model="newAttribute[input.name]" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteScadaAttribute')"
      :body="$i18n.t('dialog.body.deleteScadaAttributeConfirmation')"
      agree-text="dialog.agreeDelete"
      :loading="isLoading"
      @agree="deleteAttribute"
    />
  </div>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';

export default {
  name: 'ScadaThreeTabAttributes',
  components: {
    AdminWrapper,
    DataInput,
    DotsMenu,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    isModuleEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isFetching: true,
    isLoading: false,
    isAddingDialogVisible: false,
    isAddingDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    newAttribute: {
      datasource: null,
      attribute_id: null,
      display_attribute: null,
    },
    attributeToDelete: null,
    items: [],
    search: '',
  }),
  computed: {
    dataSourcesMetadata: get('layers/metadata'),
    dataSources() {
      return this.$_objectToArray(this.dataSourcesMetadata);
    },
    dataSource() {
      return this.dataSources.find(dataSource => dataSource.name === this.newAttribute.datasource);
    },
    dataSourceIntegerAttributes() {
      return this.dataSource?.attributes_schema.attributes.filter(attr => attr.data_type.name === 'integer') || [];
    },
    dataSourceNumberAttributes() {
      return (
        this.dataSource?.attributes_schema.attributes.filter(attr =>
          ['integer', 'decimal', 'float'].includes(attr.data_type.name)
        ) || []
      );
    },
    scadaInputs() {
      const inputs = [
        {
          name: 'datasource',
          translationPath: 'dialog.dataSource',
          items: this.dataSources,
          itemValue: 'name',
        },
        {
          name: 'attribute_id',
          translationPath: 'dialog.attribute',
          items: this.dataSourceIntegerAttributes,
          itemValue: 'id',
          disabled: !this.dataSource,
        },
        {
          name: 'display_attribute',
          translationPath: 'dialog.currentValueAttribute',
          items: this.dataSourceNumberAttributes,
          itemValue: 'id',
          disabled: !this.dataSource,
          clearable: true,
        },
      ];
      return inputs.map(input => {
        return {
          ...input,
          dataType: { name: 'select' },
          itemText: 'verbose_name',
          cols: 12,
          leftCol: 5,
          rightCol: 6,
          spacer: true,
          ...(input.name === 'display_attribute' ? {} : { rules: [v => !!v || v == '0' || 'rules.required'] }),
        };
      });
    },
    inputs() {
      return [
        {
          name: 'table',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
        },
      ];
    },
    dotsItems() {
      return [{ name: 'delete' }];
    },
    headers() {
      const inputs = ['datasource_name', 'attribute_name', 'display_attribute'];
      return [
        ...inputs.map(value => {
          return {
            text: this.$i18n.t(`admin.${value}`),
            value: value,
            align: 'center',
          };
        }),
        {
          value: 'actions',
          align: 'center',
          width: '50px',
        },
      ];
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    }
  },
  methods: {
    getScadaAttributes: call('scada/getScadaThreeAttributes'),
    addScadaAttribute: call('scada/addScadaThreeAttribute'),
    deleteScadaAttribute: call('scada/deleteScadaThreeAttribute'),
    async init() {
      try {
        this.isFetching = true;
        const items = await this.getScadaAttributes();
        this.items = items.map(item => {
          const { scada3_display_attribute, datasource } = item;
          const displayAttributeMetadata = this.dataSourcesMetadata[datasource]?.attributes_schema.attributes.find(
            attribute => attribute.id === scada3_display_attribute
          );
          const displayAttributeVerboseName = displayAttributeMetadata?.verbose_name;
          const mappedItem = { ...item, display_attribute: displayAttributeVerboseName };
          return mappedItem;
        });
      } catch {
        //
      } finally {
        this.isFetching = false;
      }
    },
    openAddingDialog() {
      this.isAddingDialogVisible = true;
      this.isAddingDialogLoaded = true;
    },
    openDeletingDialog(item) {
      this.isDeleteDialogVisible = true;
      this.isDeleteDialogLoaded = true;
      this.attributeToDelete = item.attribute_id;
    },
    async addNewAttribute() {
      try {
        this.isLoading = true;
        const data = {
          attribute_id: this.newAttribute.attribute_id,
          display_attribute: this.newAttribute.display_attribute,
        };
        await this.addScadaAttribute(data);
        this.init();
      } catch {
        //
      } finally {
        this.isLoading = false;
        this.isAddingDialogVisible = false;
        this.isAddingDialogLoaded = false;
        this.newAttribute = {
          datasource: null,
          attribute_id: null,
          display_attribute: null,
        };
      }
    },
    async deleteAttribute() {
      try {
        this.isLoading = true;
        await this.deleteScadaAttribute(this.attributeToDelete);
        this.init();
      } catch {
        //
      } finally {
        this.isLoading = false;
        this.isDeleteDialogVisible = false;
        this.isDeleteDialogLoaded = false;
        this.attributeToDelete = null;
      }
    },
  },
};
</script>
